import React, { useEffect } from 'react'
import BlockContent from '../components/block-content'
import { Link, graphql } from 'gatsby'
import SEO from '../components/seo'
import Figure from '../components/figure'
import { format } from 'date-fns'
import nb from 'date-fns/locale/nb'
import style from '../components/blog-post.module.css'
import { mapEdgesToNodes } from '../utils/helpers'
import BlogPostPreview from '../components/blog-post-preview'
import Breadcrumbs from '../components/breadcrumbs'
import slugify from 'slugify'
import { BlockContentToText } from '../utils/block-content-to-text'

const BlogPostTemplate = (props) => {
  const { data, errors } = props
  const blog = data && data.blog
  const postsFromSameCategory = data && data.postsFromSameCategory && mapEdgesToNodes(data.postsFromSameCategory)
  const metaDescription = (blog.seo && blog.seo.description) || BlockContentToText(blog.lead)

  return (
    <>
      {blog && <SEO page={blog} description={metaDescription} />}
      <Breadcrumbs crumbs={[
        { title: 'Blogg', slug: { current: 'blogg' } },
        blog.category,
        blog
      ]} />
      <div className='bg-peach-lighter bg-opacity-50 header-offset'>
        <div className='max-w-screen-lg mx-auto p-5 md:px-10 mt-10 md:mt-20 lg:px-10'>
          <article>
            <div className="mb-8">
              {blog.category.title && (
                <Link to={`/blogg/${blog.category.slug.current}`}>
                  <div className='mb-3 uppercase tracking-wide label bg-red-light text-red inline-block rounded-sm py-1 px-2'>
                    {blog.category.title}
                  </div>
                </Link>
              )}
              <h1>{blog.title}</h1>
              {blog.lead && (
                <BlockContent className='lead mb-8' blocks={blog.lead} />
              )}
              <div className='meta'>
                <div>
                  {blog.author && blog.author.name && (
                    <div className="flex items-center">
                      <Figure className='overflow-hidden rounded-full w-16 mr-4' node={blog.author.image} aspectRatio={1} hideCaption={true} />

                      <div>
                        <div className="text-sm">
                          <span>Av </span>
                          <Link to={'/vare-leger#' + slugify(blog.author.name, {lower: true})} className="text-primary">
                            {blog.author.name}
                          </Link>
                          <span> – {blog.author.position} hos Maja</span>
                        </div>
                        {blog.updatedAt && (
                          <div className="pt-1 text-sm">
                            <time datetime={format(new Date(blog.updatedAt))} className='mr-8 md:mr-16'>{format(new Date(blog.updatedAt), 'Do MMMM YYYY', { locale: nb })}</time>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {blog.updatedAt && !blog.author && (
                    <div className="pt-2 text-sm">
                      <time datetime={format(new Date(blog.updatedAt))} className='mr-8 md:mr-16'>{format(new Date(blog.updatedAt), 'Do MMMM YYYY', { locale: nb })}</time>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={`mb-10 md:mb-15 ${style.article}`}>
              <Figure node={blog.image} />
            </div>
            {blog.body && (
              <div className={style.article}>
                <BlockContent className='body editorial' blocks={blog.body} />
              </div>
            )}

            <footer>
            {blog.medicallyApprovedBy && blog.medicallyApprovedBy.name && (
              <div className="flex items-center justify-center mt-10">
                <Figure className='overflow-hidden rounded-full w-16 mr-4' node={blog.medicallyApprovedBy.image} aspectRatio={1} hideCaption={true} />

                <div>
                  <div className="text-sm">
                    <span>Medisinsk godkjent av </span>
                    <Link to={'/vare-leger#' + slugify(blog.medicallyApprovedBy.name, {lower: true})} className="text-primary">
                      {blog.medicallyApprovedBy.name}
                    </Link>
                    <div>{blog.medicallyApprovedBy.position} hos Maja</div>
                  </div>
                </div>
              </div>
            )}
            </footer>
          </article>
        </div>
      </div>
      {postsFromSameCategory.length > 0 && (
        <section className='bg-peach-lighter bg-opacity-50 py-12 md:py-20'>
          <div className='max-w-screen-xl mx-auto'>
            <h2 className='font-serif font-bold text-primary text-center mb-20'>Lyst til å lære mer?</h2>
            <div className='mx-5 md:mx-10 lg:mx-20 grid md:grid-cols-2 md:gap-10 lg:gap-16'>
              {postsFromSameCategory.map(node => (
                <BlogPostPreview className='w-full' key={node.id} {...node} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  )
}

export default BlogPostTemplate

export const query = graphql`
  query BlogQuery($id: String!, $categoryId: String!) {
    blog: sanityBlog(id: {eq: $id}) {
      id
      title
      updatedAt
      estimatedReadingTime
      image: featuredImage {
        alt
        caption
        asset {
          fluid(maxWidth: 944) {
            ...GatsbySanityImageFluid
          }
        }
      }
      author {
        name
        position
        image {
          alt
          caption
          asset {
            fluid(maxWidth: 300) {
              ...GatsbySanityImageFluid
            }
          }
        }
      }
      medicallyApprovedBy {
        name
        position
        image {
          alt
          caption
          asset {
            fluid(maxWidth: 300) {
              ...GatsbySanityImageFluid
            }
          }
        }
      }
      category {
        title
        slug {
          current
        }
      }
      lead: _rawLead(resolveReferences:{maxDepth:100})
      body: _rawBody(resolveReferences:{maxDepth:100})
      seo {
        title
        description
      }
    }
    postsFromSameCategory: allSanityBlog(filter: {
      category: { id: { eq: $categoryId } },
      id: { ne: $id }
    }, limit: 2) {
      edges {
        node {
          ...BlogPostPreview
        }
      }
    }
  }
`